import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FiSearch,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiImage,
  FiFilter,
  FiArrowUp,
  FiArrowDown,
  FiCalendar,
  FiUpload,
  FiX,
  FiFolder,
  FiTag,
  FiChevronDown,
  FiChevronUp,
  FiList,
  FiSave,
  FiType,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { FaBlog, FaLayerGroup } from "react-icons/fa";

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [viewingBlog, setViewingBlog] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [showContentEditor, setShowContentEditor] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    category: "",
    title: "",
    content: "",
    frontend_status: true,
    blogsstatus: false,
    status: "active",
    expired_date: "",
    image: null,
    images: null,
  });

  const API_BASE = "https://mithahomes.com/backend/api/blogs";

  // Statistics
  const stats = {
    total: blogs.length,
    active: blogs.filter((b) => b.status === "active").length,
    inactive: blogs.filter((b) => b.status === "inactive").length,
    categories: categories.length,
  };

  // Fetch all blogs
  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/all`);
      if (response.data.blogs) {
        setBlogs(response.data.blogs);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
      toast.error("❌ Error loading blogs");
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE}/categories`);
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, []);

  // Add new category
  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      toast.error("❌ Please enter a category name");
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/categories`, {
        category_name: newCategory.trim(),
      });

      if (response.data.success) {
        toast.success("✅ Category added successfully");
        setNewCategory("");
        setShowCategoryModal(false);
        fetchCategories();
      }
    } catch (error) {
      console.error("Error adding category:", error);
      const errorMsg = error.response?.data?.error || "Error adding category";
      toast.error(`❌ ${errorMsg}`);
    }
  };

  // Filter blogs
  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch =
      blog.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.content?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      filterCategory === "all" || blog.category === filterCategory;
    const matchesStatus =
      filterStatus === "all" || blog.status === filterStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Sort blogs
  const sortedBlogs = React.useMemo(() => {
    if (!sortConfig.key) return filteredBlogs;

    return [...filteredBlogs].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredBlogs, sortConfig]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBlogs = sortedBlogs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedBlogs.length / itemsPerPage);

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Enhanced form validation
  const validateForm = () => {
    const errors = [];

    if (!formData.category.trim()) {
      errors.push("Please select a category");
    }
    if (!formData.title.trim()) {
      errors.push("Please enter a title");
    }
    if (formData.title.length > 200) {
      errors.push("Title must be less than 200 characters");
    }
    if (!editingBlog && !formData.image) {
      errors.push("Please upload a main image");
    }

    if (errors.length > 0) {
      errors.forEach((error) => toast.error(`❌ ${error}`));
      return false;
    }

    return true;
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    const name = e.target.name;

    if (name === "image") {
      const file = files[0];
      if (file) {
        if (!file.type.startsWith("image/")) {
          toast.error("❌ Please select a valid image file");
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          toast.error("❌ Image size should be less than 5MB");
          return;
        }

        setFormData((prev) => ({ ...prev, image: file }));

        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result);
        reader.readAsDataURL(file);
      }
    } else if (name === "images") {
      // Handle multiple images
      const imageFiles = Array.from(files);
      const validImages = imageFiles.filter(
        (file) =>
          file.type.startsWith("image/") && file.size <= 5 * 1024 * 1024,
      );

      if (validImages.length !== imageFiles.length) {
        toast.error("❌ Some files are invalid or too large");
      }

      setFormData((prev) => ({
        ...prev,
        images: validImages,
      }));
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, image: null }));
    setImagePreview(null);
  };

  const removeMultipleImage = (index) => {
    const newImages = [...(formData.images || [])];
    newImages.splice(index, 1);
    setFormData((prev) => ({ ...prev, images: newImages }));
  };

  const resetForm = () => {
    setFormData({
      category: "",
      title: "",
      content: "",
      frontend_status: true,
      blogsstatus: false,
      status: "active",
      expired_date: "",
      image: null,
      images: null,
    });
    setEditingBlog(null);
    setImagePreview(null);
    setCurrentPage(1);
  };

  // API call helper function
  const apiCall = async (method, endpoint, data = null) => {
    try {
      const config = {
        method,
        url: `${API_BASE}${endpoint}`,
        headers:
          data instanceof FormData
            ? { "Content-Type": "multipart/form-data" }
            : { "Content-Type": "application/json" },
      };

      if (data) {
        config.data = data;
      }

      const response = await axios(config);

      if (response.data.success === false && response.data.error) {
        throw new Error(response.data.error);
      }

      return response.data;
    } catch (error) {
      console.error(`API Error (${method} ${endpoint}):`, error);

      if (error.response?.status === 401) {
        toast.error("❌ Session expired. Please login again.");
        // Redirect to login if needed
        // window.location.href = '/login';
      } else if (error.response?.status === 403) {
        toast.error("❌ Permission denied");
      } else if (error.response?.status === 404) {
        toast.error("❌ Resource not found");
      } else if (error.response?.status >= 500) {
        toast.error("❌ Server error. Please try again later.");
      }

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "An error occurred";

      throw new Error(errorMessage);
    }
  };

  // Add new blog or update existing
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setUploading(true);
      const submitData = new FormData();

      submitData.append("category", formData.category);
      submitData.append("title", formData.title);
      submitData.append("content", formData.content || "");
      submitData.append("frontend_status", formData.frontend_status ? 1 : 0);
      submitData.append("blogsstatus", formData.blogsstatus ? 1 : 0);
      submitData.append("status", formData.status);
      submitData.append("expired_date", formData.expired_date || "");

      if (formData.image) {
        submitData.append("image", formData.image);
      }

      if (formData.images) {
        formData.images.forEach((image, index) => {
          submitData.append("images", image);
        });
      }

      let response;

      if (editingBlog) {
        // UPDATE endpoint
        response = await apiCall("put", `/edit/${editingBlog.id}`, submitData);
        toast.success("✅ Blog updated successfully!");
      } else {
        // CREATE endpoint
        response = await apiCall("post", "/add", submitData);
        toast.success("✅ Blog created successfully!");
      }

      if (response.success) {
        setShowModal(false);
        resetForm();
        fetchBlogs();
      }
    } catch (error) {
      console.error("Error saving blog:", error);
      toast.error(`❌ ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Edit blog
  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      category: blog.category || "",
      title: blog.title || "",
      content: blog.content || "",
      frontend_status: blog.frontend_status === 1,
      blogsstatus: blog.blogsstatus === 1,
      status: blog.status || "active",
      expired_date: blog.expired_date || "",
      image: null,
      images: null,
    });

    // Handle image preview
    if (blog.image) {
      const imageUrl = getImageUrl(blog.image);
      if (imageUrl) {
        setImagePreview(imageUrl);
      }
    }

    setShowModal(true);
  };

  // View blog details
  const handleView = (blog) => {
    setViewingBlog(blog);
    setShowViewModal(true);
  };

  // Toggle status
  const toggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === "active" ? "inactive" : "active";
      const response = await apiCall("put", `/edit/${id}`, {
        status: newStatus,
      });

      if (response.success) {
        toast.success(
          `✅ Blog ${newStatus === "active" ? "activated" : "deactivated"}!`,
        );
        fetchBlogs();
      }
    } catch (error) {
      toast.error(`❌ ${error.message}`);
    }
  };

  // Delete blog
  const handleDelete = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this blog? This action cannot be undone.",
      )
    ) {
      try {
        const response = await apiCall("delete", `/${id}`);

        if (response.message) {
          toast.success("🗑️ Blog deleted successfully!");
          fetchBlogs();
          setCurrentPage(1); // Reset to first page
        }
      } catch (error) {
        toast.error(`❌ ${error.message}`);
      }
    }
  };

  // Get image URL - FIXED VERSION
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;

    // If it's already a full URL, return as is
    if (imagePath.startsWith("http")) return imagePath;

    // If it starts with /uploads, prepend the full backend URL
    if (imagePath.startsWith("/uploads")) {
      return `https://mithahomes.com/backend${imagePath}`;
    }

    // If it's just a filename, prepend the full path
    return `https://mithahomes.com/backend/uploads/${imagePath}`;
  };

  // Get status color
  const getStatusColor = (status) => {
    return status === "active"
      ? { background: "#d1fae5", color: "#065f46", border: "#a7f3d0" }
      : { background: "#fef3c7", color: "#92400e", border: "#fde68a" };
  };

  // Get category color
  const getCategoryColor = (category) => {
    const colors = {
      technology: {
        background: "#e0e7ff",
        color: "#3730a3",
        border: "#c7d2fe",
      },
      business: { background: "#d1fae5", color: "#059669", border: "#a7f3d0" },
      lifestyle: { background: "#fce7f3", color: "#be185d", border: "#fbcfe8" },
      health: { background: "#dcfce7", color: "#166534", border: "#bbf7d0" },
      travel: { background: "#fef3c7", color: "#92400e", border: "#fde68a" },
      food: { background: "#fed7aa", color: "#c2410c", border: "#fdba74" },
      default: { background: "#f3f4f6", color: "#374151", border: "#e5e7eb" },
    };

    const categoryKey = category?.toLowerCase() || "default";
    return colors[categoryKey] || colors.default;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  // Truncate content
  const truncateContent = (text, length = 100) => {
    if (!text) return "No content";
    const plainText = text.replace(/<[^>]*>/g, ""); // Remove HTML tags
    return plainText.length > length
      ? plainText.substring(0, length) + "..."
      : plainText;
  };

  // Pagination controls
  const PaginationControls = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "8px",
          marginTop: "20px",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          style={{
            padding: "8px 12px",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            background: currentPage === 1 ? "#f1f5f9" : "white",
            color: currentPage === 1 ? "#94a3b8" : "#374151",
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            fontSize: "0.875rem",
          }}
        >
          <FiChevronLeft />
          Previous
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => setCurrentPage(1)}
              style={{
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                background: "white",
                color: "#374151",
                cursor: "pointer",
              }}
            >
              1
            </button>
            {startPage > 2 && <span>...</span>}
          </>
        )}

        {pageNumbers.map((page) => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            style={{
              padding: "8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              background: currentPage === page ? "#667eea" : "white",
              color: currentPage === page ? "white" : "#374151",
              cursor: "pointer",
              fontWeight: currentPage === page ? "600" : "normal",
            }}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span>...</span>}
            <button
              onClick={() => setCurrentPage(totalPages)}
              style={{
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                background: "white",
                color: "#374151",
                cursor: "pointer",
              }}
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          disabled={currentPage === totalPages}
          style={{
            padding: "8px 12px",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            background: currentPage === totalPages ? "#f1f5f9" : "white",
            color: currentPage === totalPages ? "#94a3b8" : "#374151",
            cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            fontSize: "0.875rem",
          }}
        >
          Next
          <FiChevronRight />
        </button>

        <div
          style={{
            marginLeft: "20px",
            fontSize: "0.875rem",
            color: "#64748b",
          }}
        >
          Page {currentPage} of {totalPages}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "20px",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "30px",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: "700",
              color: "#1e293b",
              margin: "0 0 8px 0",
            }}
          >
            Blog Management
          </h1>
          <p
            style={{
              fontSize: "1.1rem",
              color: "#64748b",
              margin: "0",
            }}
          >
            Create, manage and publish blog content
          </p>
        </div>
        <div style={{ display: "flex", gap: "12px" }}>
          <button
            onClick={() => setShowCategoryModal(true)}
            style={{
              background: "#10b981",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "12px",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
              transition: "all 0.3s ease",
            }}
            onMouseOver={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 8px 25px rgba(16, 185, 129, 0.4)";
            }}
            onMouseOut={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 15px rgba(16, 185, 129, 0.3)";
            }}
          >
            <FiTag />
            Add Category
          </button>
          <button
            onClick={() => setShowModal(true)}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "12px",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
              transition: "all 0.3s ease",
            }}
            onMouseOver={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 8px 25px rgba(102, 126, 234, 0.4)";
            }}
            onMouseOut={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 15px rgba(102, 126, 234, 0.3)";
            }}
          >
            <FiPlus />
            Add New Blog
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        {[
          {
            title: "Total Blogs",
            value: stats.total,
            icon: <FaBlog />,
            color: "linear-gradient(135deg, #667eea, #764ba2)",
          },
          {
            title: "Active Blogs",
            value: stats.active,
            icon: <FiEye />,
            color: "linear-gradient(135deg, #10b981, #059669)",
          },
          {
            title: "Categories",
            value: stats.categories,
            icon: <FaLayerGroup />,
            color: "linear-gradient(135deg, #f59e0b, #d97706)",
          },
          {
            title: "Inactive Blogs",
            value: stats.inactive,
            icon: <FiEyeOff />,
            color: "linear-gradient(135deg, #ef4444, #dc2626)",
          },
        ].map((stat, index) => (
          <div
            key={index}
            style={{
              background: "white",
              padding: "25px",
              borderRadius: "16px",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              display: "flex",
              alignItems: "center",
              gap: "20px",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow =
                "0 12px 25px rgba(0, 0, 0, 0.15)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
            }}
          >
            <div
              style={{
                width: "60px",
                height: "60px",
                borderRadius: "12px",
                background: stat.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.5rem",
                color: "white",
              }}
            >
              {stat.icon}
            </div>
            <div>
              <h3
                style={{
                  fontSize: "2rem",
                  fontWeight: "700",
                  color: "#1e293b",
                  margin: "0 0 4px 0",
                }}
              >
                {stat.value}
              </h3>
              <p
                style={{
                  fontSize: "0.95rem",
                  color: "#64748b",
                  margin: "0",
                }}
              >
                {stat.title}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Controls Bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "20px",
          marginBottom: "25px",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            position: "relative",
            flex: "1",
            minWidth: "300px",
          }}
        >
          <FiSearch
            style={{
              position: "absolute",
              left: "15px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#94a3b8",
              fontSize: "1.1rem",
            }}
          />
          <input
            type="text"
            placeholder="Search blogs by title, category or content..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page when searching
            }}
            style={{
              width: "100%",
              padding: "12px 20px 12px 45px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "1rem",
              background: "white",
              transition: "all 0.3s ease",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#667eea";
              e.target.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e2e8f0";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: "12px 16px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "1rem",
              background: "white",
              cursor: "pointer",
              minWidth: "140px",
            }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            value={filterCategory}
            onChange={(e) => {
              setFilterCategory(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: "12px 16px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "1rem",
              background: "white",
              cursor: "pointer",
              minWidth: "140px",
            }}
          >
            <option value="all">All Categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.category_name}>
                {category.category_name}
              </option>
            ))}
          </select>

          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value));
              setCurrentPage(1);
            }}
            style={{
              padding: "12px 16px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "1rem",
              background: "white",
              cursor: "pointer",
              minWidth: "120px",
            }}
          >
            <option value="5">5 per page</option>
            <option value="10">10 per page</option>
            <option value="20">20 per page</option>
            <option value="50">50 per page</option>
          </select>
        </div>
      </div>

      {/* Data Table */}
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          marginBottom: "30px",
        }}
      >
        {loading ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 40px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f1f5f9",
                borderTop: "4px solid #667eea",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            ></div>
            <p
              style={{
                color: "#64748b",
                fontSize: "1rem",
                margin: "0",
              }}
            >
              Loading blogs...
            </p>
          </div>
        ) : sortedBlogs.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 40px",
              color: "#64748b",
            }}
          >
            <FiFilter
              style={{
                fontSize: "4rem",
                color: "#cbd5e1",
                marginBottom: "20px",
              }}
            />
            <h3
              style={{
                fontSize: "1.5rem",
                color: "#475569",
                margin: "0 0 12px 0",
              }}
            >
              No blogs found
            </h3>
            <p
              style={{
                fontSize: "1rem",
                margin: "0 0 25px 0",
              }}
            >
              Try adjusting your search or filter criteria
            </p>
            <button
              onClick={() => {
                setSearchTerm("");
                setFilterCategory("all");
                setFilterStatus("all");
                setCurrentPage(1);
              }}
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "12px",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      background: "#f8fafc",
                      padding: "16px 20px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#475569",
                      borderBottom: "2px solid #e2e8f0",
                      fontSize: "0.9rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("title")}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      Blog
                      {sortConfig.key === "title" &&
                        (sortConfig.direction === "asc" ? (
                          <FiArrowUp />
                        ) : (
                          <FiArrowDown />
                        ))}
                    </span>
                  </th>
                  <th
                    style={{
                      background: "#f8fafc",
                      padding: "16px 20px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#475569",
                      borderBottom: "2px solid #e2e8f0",
                      fontSize: "0.9rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("category")}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      Category
                      {sortConfig.key === "category" &&
                        (sortConfig.direction === "asc" ? (
                          <FiArrowUp />
                        ) : (
                          <FiArrowDown />
                        ))}
                    </span>
                  </th>
                  <th
                    style={{
                      background: "#f8fafc",
                      padding: "16px 20px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#475569",
                      borderBottom: "2px solid #e2e8f0",
                      fontSize: "0.9rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("status")}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      Status
                      {sortConfig.key === "status" &&
                        (sortConfig.direction === "asc" ? (
                          <FiArrowUp />
                        ) : (
                          <FiArrowDown />
                        ))}
                    </span>
                  </th>
                  <th
                    style={{
                      background: "#f8fafc",
                      padding: "16px 20px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#475569",
                      borderBottom: "2px solid #e2e8f0",
                      fontSize: "0.9rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("created_at")}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      Created
                      {sortConfig.key === "created_at" &&
                        (sortConfig.direction === "asc" ? (
                          <FiArrowUp />
                        ) : (
                          <FiArrowDown />
                        ))}
                    </span>
                  </th>
                  <th
                    style={{
                      background: "#f8fafc",
                      padding: "16px 20px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#475569",
                      borderBottom: "2px solid #e2e8f0",
                      fontSize: "0.9rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentBlogs.map((blog) => (
                  <tr
                    key={blog.id}
                    style={{
                      transition: "background 0.3s ease",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = "#f8fafc";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = "white";
                    }}
                  >
                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "15px",
                        }}
                      >
                        {blog.image ? (
                          <div
                            style={{
                              position: "relative",
                              width: "80px",
                              height: "60px",
                              borderRadius: "8px",
                              overflow: "hidden",
                              flexShrink: "0",
                              cursor: "pointer",
                            }}
                            onClick={() => handleView(blog)}
                          >
                            <img
                              src={getImageUrl(blog.image)}
                              alt={blog.title}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.target.src =
                                  "https://via.placeholder.com/80x60?text=No+Image";
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            style={{
                              width: "80px",
                              height: "60px",
                              borderRadius: "8px",
                              background: "#f1f5f9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#94a3b8",
                              flexShrink: "0",
                            }}
                          >
                            <FiImage style={{ fontSize: "1.2rem" }} />
                          </div>
                        )}
                        <div style={{ flex: "1" }}>
                          <h4
                            style={{
                              fontWeight: "600",
                              color: "#1e293b",
                              margin: "0 0 6px 0",
                              fontSize: "1rem",
                              cursor: "pointer",
                            }}
                            onClick={() => handleView(blog)}
                          >
                            {blog.title}
                          </h4>
                          <p
                            style={{
                              color: "#64748b",
                              fontSize: "0.875rem",
                              margin: "0",
                              lineHeight: "1.4",
                            }}
                          >
                            {truncateContent(blog.content, 80)}
                          </p>
                          <p
                            style={{
                              color: "#94a3b8",
                              fontSize: "0.75rem",
                              margin: "4px 0 0 0",
                            }}
                          >
                            Code: {blog.blogscontetcount || "N/A"}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 12px",
                          borderRadius: "20px",
                          fontSize: "0.8rem",
                          fontWeight: "600",
                          background: getCategoryColor(blog.category)
                            .background,
                          color: getCategoryColor(blog.category).color,
                          border: `1px solid ${getCategoryColor(blog.category).border}`,
                        }}
                      >
                        <FiTag style={{ fontSize: "0.7rem" }} />
                        {blog.category || "Uncategorized"}
                      </div>
                    </td>

                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 12px",
                          borderRadius: "20px",
                          fontSize: "0.8rem",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          background: getStatusColor(blog.status).background,
                          color: getStatusColor(blog.status).color,
                          cursor: "pointer",
                        }}
                        onClick={() => toggleStatus(blog.id, blog.status)}
                        title={`Click to ${blog.status === "active" ? "deactivate" : "activate"}`}
                      >
                        {blog.status === "active" ? (
                          <>
                            <FiEye style={{ fontSize: "0.8rem" }} />
                            Active
                          </>
                        ) : (
                          <>
                            <FiEyeOff style={{ fontSize: "0.8rem" }} />
                            Inactive
                          </>
                        )}
                      </div>
                      {blog.frontend_status === 1 && (
                        <div
                          style={{
                            fontSize: "0.7rem",
                            color: "#10b981",
                            marginTop: "4px",
                            fontWeight: "500",
                          }}
                        >
                          Frontend: Visible
                        </div>
                      )}
                      {blog.blogsstatus === 1 && (
                        <div
                          style={{
                            fontSize: "0.7rem",
                            color: "#3b82f6",
                            marginTop: "4px",
                            fontWeight: "500",
                          }}
                        >
                          Featured Blog
                        </div>
                      )}
                    </td>

                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          color: "#64748b",
                          fontSize: "0.875rem",
                        }}
                      >
                        <FiCalendar style={{ fontSize: "0.9rem" }} />
                        {blog.created_at ? formatDate(blog.created_at) : "N/A"}
                      </div>
                      {blog.expired_date && (
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "#f59e0b",
                            marginTop: "4px",
                            fontWeight: "500",
                          }}
                        >
                          Expires: {formatDate(blog.expired_date)}
                        </div>
                      )}
                    </td>

                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          onClick={() => handleView(blog)}
                          style={{
                            background: "#3b82f6",
                            color: "white",
                            border: "none",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          title="View Details"
                        >
                          <FiEye />
                        </button>

                        <button
                          onClick={() => handleEdit(blog)}
                          style={{
                            background: "#f1f5f9",
                            color: "#475569",
                            border: "1px solid #e2e8f0",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          title="Edit"
                        >
                          <FiEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(blog.id)}
                          style={{
                            background: "#ef4444",
                            color: "white",
                            border: "none",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination Info */}
            <div
              style={{
                padding: "20px",
                borderTop: "1px solid #f1f5f9",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <div
                style={{
                  fontSize: "0.9rem",
                  color: "#64748b",
                }}
              >
                Showing {indexOfFirstItem + 1} to{" "}
                {Math.min(indexOfLastItem, sortedBlogs.length)} of{" "}
                {sortedBlogs.length} blogs
              </div>
              <PaginationControls />
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Blog Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: "1000",
            padding: "20px",
            backdropFilter: "blur(5px)",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              width: "100%",
              maxWidth: "800px",
              maxHeight: "90vh",
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
              animation: "modalSlideIn 0.3s ease",
            }}
          >
            <div
              style={{
                padding: "25px 30px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <h2
                style={{
                  margin: "0",
                  fontSize: "1.5rem",
                  fontWeight: "600",
                }}
              >
                {editingBlog ? "Edit Blog" : "Add New Blog"}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  fontSize: "1.5rem",
                  color: "white",
                  cursor: "pointer",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.3s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.3)";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.2)";
                }}
              >
                &times;
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              style={{
                padding: "30px",
                maxHeight: "calc(90vh - 100px)",
                overflowY: "auto",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "20px",
                  marginBottom: "20px",
                }}
              >
                {/* Category */}
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Category *
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "1rem",
                      background: "white",
                    }}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.category_name}>
                        {cat.category_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Title */}
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter blog title"
                    required
                    maxLength="200"
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "1rem",
                    }}
                  />
                  <p
                    style={{
                      fontSize: "0.75rem",
                      color: "#6b7280",
                      marginTop: "4px",
                      textAlign: "right",
                    }}
                  >
                    {formData.title.length}/200 characters
                  </p>
                </div>
              </div>

              {/* Expiry Date */}
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Expiry Date (Optional)
                </label>
                <input
                  type="date"
                  name="expired_date"
                  value={formData.expired_date}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split("T")[0]}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "1rem",
                  }}
                />
                <p
                  style={{
                    fontSize: "0.875rem",
                    color: "#6b7280",
                    marginTop: "4px",
                  }}
                >
                  Blog will be automatically deactivated after this date
                </p>
              </div>

              {/* Content */}
              <div style={{ marginBottom: "20px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <label
                    style={{
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Content
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowContentEditor(!showContentEditor)}
                    style={{
                      background: "#f1f5f9",
                      color: "#475569",
                      border: "1px solid #e2e8f0",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "0.875rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    {showContentEditor ? <FiChevronUp /> : <FiChevronDown />}
                    {showContentEditor ? "Hide Editor" : "Show Editor"}
                  </button>
                </div>

                {showContentEditor ? (
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder="Enter blog content (HTML supported)"
                    rows={8}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "1rem",
                      fontFamily: "monospace",
                      resize: "vertical",
                    }}
                  />
                ) : (
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder="Enter blog content (HTML supported)"
                    rows={3}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "1rem",
                      resize: "vertical",
                    }}
                  />
                )}
              </div>

              {/* Main Image Upload */}
              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  {editingBlog
                    ? "Update Main Image (Optional)"
                    : "Upload Main Image *"}
                  <span
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: "normal",
                      color: "#6b7280",
                      marginLeft: "8px",
                    }}
                  >
                    (Max 5MB, JPG/PNG)
                  </span>
                </label>

                <div
                  style={{
                    border: "2px dashed #e2e8f0",
                    borderRadius: "12px",
                    padding: "25px",
                    textAlign: "center",
                    background: imagePreview ? "transparent" : "#f9fafb",
                    transition: "all 0.3s ease",
                  }}
                >
                  {imagePreview ? (
                    <div>
                      <div
                        style={{
                          width: "200px",
                          height: "150px",
                          margin: "0 auto 15px",
                          borderRadius: "10px",
                          overflow: "hidden",
                        }}
                      >
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          justifyContent: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <label
                          style={{
                            background: "#3b82f6",
                            color: "white",
                            border: "none",
                            padding: "10px 20px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.9rem",
                            fontWeight: "500",
                          }}
                        >
                          Change Image
                          <input
                            type="file"
                            name="image"
                            accept="image/*"
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={removeImage}
                          style={{
                            background: "#ef4444",
                            color: "white",
                            border: "none",
                            padding: "10px 20px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.9rem",
                            fontWeight: "500",
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <FiUpload
                        style={{
                          fontSize: "3rem",
                          color: "#cbd5e1",
                          marginBottom: "15px",
                        }}
                      />
                      <p
                        style={{
                          color: "#6b7280",
                          marginBottom: "15px",
                        }}
                      >
                        Drag & drop or click to upload main image
                      </p>
                      <label
                        style={{
                          background: "#f1f5f9",
                          color: "#374151",
                          border: "1px solid #e2e8f0",
                          padding: "10px 25px",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "0.9rem",
                          fontWeight: "500",
                          display: "inline-block",
                        }}
                      >
                        Select Image
                        <input
                          type="file"
                          name="image"
                          accept="image/*"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Images Upload */}
              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Additional Images (Optional)
                  <span
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: "normal",
                      color: "#6b7280",
                      marginLeft: "8px",
                    }}
                  >
                    (Max 5 images, 5MB each)
                  </span>
                </label>

                <div
                  style={{
                    border: "2px dashed #e2e8f0",
                    borderRadius: "12px",
                    padding: "20px",
                    background: "#f9fafb",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      background: "#f1f5f9",
                      color: "#374151",
                      border: "1px solid #e2e8f0",
                      padding: "12px 20px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      fontWeight: "500",
                      marginBottom: formData.images?.length ? "15px" : "0",
                    }}
                  >
                    <FiUpload />
                    Select Multiple Images
                    <input
                      type="file"
                      name="images"
                      accept="image/*"
                      multiple
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                  </label>

                  {formData.images?.length > 0 && (
                    <div
                      style={{
                        marginTop: "15px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "0.875rem",
                          color: "#6b7280",
                          marginBottom: "10px",
                        }}
                      >
                        Selected images ({formData.images.length}):
                      </p>
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          flexWrap: "wrap",
                        }}
                      >
                        {formData.images.map((image, index) => (
                          <div
                            key={index}
                            style={{
                              position: "relative",
                              width: "80px",
                              height: "80px",
                              borderRadius: "8px",
                              overflow: "hidden",
                              background: "#f1f5f9",
                            }}
                          >
                            <img
                              src={URL.createObjectURL(image)}
                              alt={`Preview ${index}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => removeMultipleImage(index)}
                              style={{
                                position: "absolute",
                                top: "4px",
                                right: "4px",
                                background: "#ef4444",
                                color: "white",
                                border: "none",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                fontSize: "0.7rem",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Status, Frontend Status, and Blog Status */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "20px",
                  marginBottom: "25px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "1rem",
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#374151",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      name="blogsstatus"
                      checked={formData.blogsstatus}
                      onChange={handleInputChange}
                      style={{
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                      }}
                    />
                    Featured Blog
                  </label>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#6b7280",
                      margin: "4px 0 0 0",
                    }}
                  >
                    Mark as featured (only one allowed)
                  </p>
                </div>
              </div>

              {/* Form Actions */}
              <div
                style={{
                  paddingTop: "20px",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "15px",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  style={{
                    background: "transparent",
                    color: "#64748b",
                    border: "2px solid #e2e8f0",
                    padding: "12px 25px",
                    borderRadius: "10px",
                    fontSize: "1rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = "#f8fafc";
                    e.target.style.color = "#475569";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.color = "#64748b";
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  style={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    padding: "12px 30px",
                    borderRadius: "10px",
                    fontSize: "1rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    opacity: uploading ? 0.7 : 1,
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    if (!uploading) {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 8px 25px rgba(102, 126, 234, 0.4)";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!uploading) {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "none";
                    }
                  }}
                >
                  {uploading ? (
                    <>
                      <div
                        style={{
                          display: "inline-block",
                          width: "16px",
                          height: "16px",
                          border: "2px solid rgba(255,255,255,0.3)",
                          borderTop: "2px solid white",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                          marginRight: "8px",
                          verticalAlign: "middle",
                        }}
                      ></div>
                      {editingBlog ? "Updating..." : "Creating..."}
                    </>
                  ) : editingBlog ? (
                    "Update Blog"
                  ) : (
                    "Create Blog"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showCategoryModal && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: "1000",
            padding: "20px",
            backdropFilter: "blur(5px)",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              width: "100%",
              maxWidth: "500px",
              maxHeight: "90vh",
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
              animation: "modalSlideIn 0.3s ease",
            }}
          >
            <div
              style={{
                padding: "25px 30px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                color: "white",
              }}
            >
              <h2
                style={{
                  margin: "0",
                  fontSize: "1.5rem",
                  fontWeight: "600",
                }}
              >
                Add New Category
              </h2>
              <button
                onClick={() => setShowCategoryModal(false)}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  fontSize: "1.5rem",
                  color: "white",
                  cursor: "pointer",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.3s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.3)";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.2)";
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: "30px" }}>
              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Category Name *
                </label>
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Enter category name"
                  maxLength="50"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "1rem",
                  }}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "15px",
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowCategoryModal(false)}
                  style={{
                    background: "transparent",
                    color: "#64748b",
                    border: "2px solid #e2e8f0",
                    padding: "12px 25px",
                    borderRadius: "10px",
                    fontSize: "1rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = "#f8fafc";
                    e.target.style.color = "#475569";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.color = "#64748b";
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCategory}
                  style={{
                    background:
                      "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    color: "white",
                    border: "none",
                    padding: "12px 30px",
                    borderRadius: "10px",
                    fontSize: "1rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow =
                      "0 8px 25px rgba(16, 185, 129, 0.4)";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  Add Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showViewModal && viewingBlog && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            background: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: "1000",
            padding: "20px",
            backdropFilter: "blur(10px)",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              width: "100%",
              maxWidth: "800px",
              maxHeight: "90vh",
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.3)",
              animation: "modalSlideIn 0.3s ease",
            }}
          >
            <div
              style={{
                padding: "25px 30px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <div>
                <h2
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "1.5rem",
                    fontWeight: "600",
                  }}
                >
                  Blog Details
                </h2>
                <p
                  style={{
                    margin: "0",
                    opacity: "0.9",
                    fontSize: "0.95rem",
                  }}
                >
                  Complete information about this blog
                </p>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  fontSize: "1.5rem",
                  color: "white",
                  cursor: "pointer",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.3s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.3)";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.2)";
                }}
              >
                &times;
              </button>
            </div>

            <div
              style={{
                padding: "30px",
                maxHeight: "calc(90vh - 100px)",
                overflowY: "auto",
              }}
            >
              {viewingBlog.image && (
                <div
                  style={{
                    marginBottom: "25px",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <img
                    src={getImageUrl(viewingBlog.image)}
                    alt={viewingBlog.title}
                    style={{
                      width: "100%",
                      maxHeight: "400px",
                      objectFit: "contain",
                      background: "#f8fafc",
                      padding: "10px",
                    }}
                    onError={(e) => {
                      e.target.src =
                        "https://via.placeholder.com/800x400?text=Image+Not+Found";
                    }}
                  />
                </div>
              )}

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: "#64748b",
                    marginBottom: "6px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Title
                </label>
                <p
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "700",
                    color: "#1e293b",
                    margin: "0",
                    padding: "15px",
                    background: "#f8fafc",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  {viewingBlog.title}
                </p>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: "#64748b",
                    marginBottom: "6px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Category
                </label>
                <div
                  style={{
                    padding: "12px 16px",
                    background: getCategoryColor(viewingBlog.category)
                      .background,
                    color: getCategoryColor(viewingBlog.category).color,
                    border: `1px solid ${getCategoryColor(viewingBlog.category).border}`,
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    fontWeight: "600",
                  }}
                >
                  <FiTag style={{ fontSize: "1rem" }} />
                  {viewingBlog.category || "Uncategorized"}
                </div>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: "#64748b",
                    marginBottom: "6px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Content
                </label>
                <div
                  style={{
                    padding: "20px",
                    background: "#f8fafc",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                >
                  {viewingBlog.content ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: viewingBlog.content }}
                      style={{
                        fontSize: "1rem",
                        lineHeight: "1.6",
                        color: "#374151",
                      }}
                    />
                  ) : (
                    <p
                      style={{
                        color: "#9ca3af",
                        fontStyle: "italic",
                        textAlign: "center",
                        margin: "20px 0",
                      }}
                    >
                      No content available
                    </p>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "20px",
                  marginBottom: "20px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.875rem",
                      fontWeight: "600",
                      color: "#64748b",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Status
                  </label>
                  <div
                    style={{
                      padding: "12px 16px",
                      background: getStatusColor(viewingBlog.status).background,
                      color: getStatusColor(viewingBlog.status).color,
                      border: `1px solid ${getStatusColor(viewingBlog.status).border}`,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      fontSize: "0.875rem",
                      letterSpacing: "0.5px",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      toggleStatus(viewingBlog.id, viewingBlog.status)
                    }
                    title={`Click to ${viewingBlog.status === "active" ? "deactivate" : "activate"}`}
                  >
                    {viewingBlog.status === "active" ? <FiEye /> : <FiEyeOff />}
                    {viewingBlog.status === "active" ? "Active" : "Inactive"}
                  </div>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.875rem",
                      fontWeight: "600",
                      color: "#64748b",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Frontend Status
                  </label>
                  <div
                    style={{
                      padding: "12px 16px",
                      background:
                        viewingBlog.frontend_status === 1
                          ? "#d1fae5"
                          : "#fef3c7",
                      color:
                        viewingBlog.frontend_status === 1
                          ? "#065f46"
                          : "#92400e",
                      border: `1px solid ${viewingBlog.frontend_status === 1 ? "#a7f3d0" : "#fde68a"}`,
                      borderRadius: "10px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      fontSize: "0.875rem",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {viewingBlog.frontend_status === 1 ? "Visible" : "Hidden"}
                  </div>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.875rem",
                      fontWeight: "600",
                      color: "#64748b",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Featured Blog
                  </label>
                  <div
                    style={{
                      padding: "12px 16px",
                      background:
                        viewingBlog.blogsstatus === 1 ? "#dbeafe" : "#f3f4f6",
                      color:
                        viewingBlog.blogsstatus === 1 ? "#1e40af" : "#6b7280",
                      border: `1px solid ${viewingBlog.blogsstatus === 1 ? "#93c5fd" : "#e5e7eb"}`,
                      borderRadius: "10px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      fontSize: "0.875rem",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {viewingBlog.blogsstatus === 1 ? "Featured" : "Regular"}
                  </div>
                </div>
              </div>

              {viewingBlog.created_at && (
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.875rem",
                      fontWeight: "600",
                      color: "#64748b",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Created Date
                  </label>
                  <p
                    style={{
                      fontSize: "1rem",
                      color: "#475569",
                      margin: "0",
                      padding: "12px 16px",
                      background: "#f8fafc",
                      borderRadius: "10px",
                      border: "1px solid #e2e8f0",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <FiCalendar style={{ fontSize: "1rem" }} />
                    {formatDate(viewingBlog.created_at)}
                  </p>
                </div>
              )}

              {viewingBlog.expired_date && (
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.875rem",
                      fontWeight: "600",
                      color: "#64748b",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Expiry Date
                  </label>
                  <p
                    style={{
                      fontSize: "1rem",
                      color: "#dc2626",
                      margin: "0",
                      padding: "12px 16px",
                      background: "#fef2f2",
                      borderRadius: "10px",
                      border: "1px solid #fecaca",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <FiCalendar style={{ fontSize: "1rem" }} />
                    {formatDate(viewingBlog.expired_date)}
                  </p>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "15px",
                  marginTop: "30px",
                  paddingTop: "20px",
                  borderTop: "1px solid #e2e8f0",
                }}
              >
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(viewingBlog);
                  }}
                  style={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow =
                      "0 8px 25px rgba(102, 126, 234, 0.4)";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow =
                      "0 4px 15px rgba(102, 126, 234, 0.3)";
                  }}
                >
                  <FiEdit />
                  Edit Blog
                </button>
                <button
                  onClick={() => setShowViewModal(false)}
                  style={{
                    background: "transparent",
                    color: "#64748b",
                    border: "2px solid #e2e8f0",
                    padding: "12px 24px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: "500",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = "#f8fafc";
                    e.target.style.color = "#475569";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.color = "#64748b";
                  }}
                >
                  Close Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-50px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default Blogs;
